import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Reveal, StaggerItem } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Stamp from "@/components/Stamp";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { PAGE_SEO } from "@/lib/seo";
import teaserMegafon from "@/assets/brand/teaser_megafon.png";
import teaserBanner from "@/assets/brand/teaser_banner.png";
import pouches from "@/assets/brand/pouches.png";
import ensemble from "@/assets/brand/ensemble.jpeg";
import pouchSugar from "@/assets/brand/pouch_sugar.png";
import pouchImmune from "@/assets/brand/pouch_immune.png";
import dropCap from "@/assets/brand/drop_cap.png";
import dropTee from "@/assets/brand/drop_tee.png";
import dropButton from "@/assets/brand/drop_button.png";
import dropSticker from "@/assets/brand/drop_sticker.png";
import facepaint from "@/assets/social/facepaint.jpg";
import festival from "@/assets/social/festival.jpg";
import neon from "@/assets/social/neon.jpg";
import travel from "@/assets/social/travel.jpg";

// Editorial magazine grid. Frames with `to` link straight to the matching product page.
const FRAMES = [
  { img: teaserMegafon, caption: "FIGHT TEE × CLUB MEGAFON — DROP 01", span: "md:col-span-8", ratio: "aspect-[16/10]", to: "/product/club-megafon" },
  { img: pouchImmune, caption: "03 IMMUNE BOOSTER — STAND-UP POUCH", span: "md:col-span-4", ratio: "aspect-[4/5] md:aspect-auto md:h-full", to: "/product/immune-booster" },
  { quote: true, span: "md:col-span-4" },
  { img: teaserBanner, caption: "DEMO BANNER — ROLL IT OUT", span: "md:col-span-8", ratio: "aspect-[16/10]", to: "/product/demo-banner" },
  { img: pouches, caption: "THE ESSENTIALS — 01 / 02 / 03", span: "md:col-span-7", ratio: "aspect-[16/9]", to: "/shop" },
  { img: facepaint, caption: "THE CROWD — FESTIVAL EDIT", span: "md:col-span-5", ratio: "aspect-[4/5] md:aspect-auto md:h-full", to: "/creator/leaonthemove" },
  { img: ensemble, caption: "THE BRAND ENSEMBLE", span: "md:col-span-5", ratio: "aspect-square", to: "/movement" },
  { img: dropCap, caption: "DEMO CAP — PINK CORD", span: "md:col-span-3", ratio: "aspect-square", to: "/product/demo-cap" },
  { img: neon, caption: "NIGHT MODE — @miakampft", span: "md:col-span-4", ratio: "aspect-[4/5] md:aspect-auto md:h-full", to: "/creator/miakampft" },
  { img: festival, caption: "CLUB MEETUP — THE CROWD", span: "md:col-span-4", ratio: "aspect-square", to: "/creators" },
  { img: dropTee, caption: "FIGHT TEE — NO CRASH. NO STRESS. NO 3AM.", span: "md:col-span-4", ratio: "aspect-square", to: "/product/fight-tee" },
  { img: travel, caption: "ON THE MOVE — @leaonthemove", span: "md:col-span-4", ratio: "aspect-[4/5] md:aspect-auto md:h-full", to: "/creator/leaonthemove" },
  { img: dropButton, caption: "FFY BUTTON — YOU GOT THIS.", span: "md:col-span-4", ratio: "aspect-square", to: "/product/ffy-button" },
  { img: pouchSugar, caption: "01 SUGAR BALANCE — STAND-UP POUCH", span: "md:col-span-4", ratio: "aspect-square", to: "/product/sugar-balance" },
  { img: dropSticker, caption: "GRAFFITI STICKER — STREET EDITION", span: "md:col-span-4", ratio: "aspect-square", to: "/product/sticker-pack" },
];

const Lookbook = () => {
  const { t } = useLang();
  const l = t.lookbook;

  return (
    <div data-testid="page-lookbook">
      <Seo {...PAGE_SEO.lookbook} jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Lookbook", path: "/lookbook" }])]} />
      {/* Header */}
      <section className="bg-limewash border-b border-ink py-14 md:py-20 relative overflow-hidden">
        <div className="absolute right-6 md:right-20 top-6 hidden md:block opacity-90">
          <Stamp size={96} bg="bg-cream" />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 relative z-10">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="kicker text-ink/60">{l.kicker}</p>
              <p className="kicker text-pink">{l.edition}</p>
            </div>
            <h1 className="display uppercase text-[18vw] md:text-[11vw] leading-[0.84] mt-4 text-brand">
              {l.title}
            </h1>
            <p className="mt-5 max-w-xl text-lg font-medium text-ink/70">{l.sub}</p>
          </Reveal>
        </div>
      </section>

      {/* Magazine grid */}
      <section className="bg-cream py-10 md:py-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="kicker text-ink/50">Index</p>
            <p className="kicker text-ink/50">{FRAMES.length} {l.frames}</p>
          </div>

          <StaggerItem>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-ink border border-ink">
              {FRAMES.map((f, i) =>
                f.quote ? (
                  <div
                    key={i}
                    className={`bg-lime p-8 md:p-10 flex flex-col justify-center ${f.span}`}
                    data-testid={`lookbook-quote`}
                  >
                    <p className="display uppercase text-4xl md:text-5xl leading-[0.9] text-ink">{l.quote}</p>
                    <p className="kicker text-ink/60 mt-4">{l.quoteSub}</p>
                    <p className="display text-5xl text-pink mt-6">{String(i + 1).padStart(2, "0")}</p>
                  </div>
                ) : (
                  <figure key={i} className={`group bg-cream flex flex-col ${f.span}`} data-testid={`lookbook-frame-${i}`}>
                    {f.to ? (
                      <Link to={f.to} className="flex flex-col flex-1" aria-label={f.caption}>
                        <div className={`relative overflow-hidden flex-1 ${f.ratio || ""}`}>
                          <img
                            src={f.img}
                            alt={f.caption}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                          />
                          <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-cream border border-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <ArrowUpRight className="w-4 h-4 text-ink" strokeWidth={2} />
                          </span>
                        </div>
                        <figcaption className="flex items-center justify-between gap-3 border-t border-ink px-4 py-3 group-hover:bg-limewash transition-colors duration-300">
                          <span className="kicker text-[10px] text-ink/60">{f.caption}</span>
                          <span className="display text-xl text-pink leading-none">{String(i + 1).padStart(2, "0")}</span>
                        </figcaption>
                      </Link>
                    ) : (
                      <>
                        <div className={`relative overflow-hidden flex-1 ${f.ratio || ""}`}>
                          <img
                            src={f.img}
                            alt={f.caption}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                          />
                        </div>
                        <figcaption className="flex items-center justify-between gap-3 border-t border-ink px-4 py-3">
                          <span className="kicker text-[10px] text-ink/60">{f.caption}</span>
                          <span className="display text-xl text-pink leading-none">{String(i + 1).padStart(2, "0")}</span>
                        </figcaption>
                      </>
                    )}
                  </figure>
                )
              )}
            </div>
          </StaggerItem>

          {/* CTA */}
          <Reveal className="mt-10">
            <div className="border border-ink bg-pink text-cream p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <h2 className="display uppercase text-4xl md:text-6xl leading-[0.85]">{l.ctaTitle}</h2>
              <Link to="/shop" data-testid="lookbook-cta" className="btn bg-cream text-ink h-14 px-8 text-base hover:bg-ink hover:text-cream flex-shrink-0">
                {l.cta} <ArrowRight className="w-5 h-5" strokeWidth={2} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee text={t.marquee} bg="bg-lav" color="text-ink" />
    </div>
  );
};

export default Lookbook;
